import { useState } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import DropdownFilter from '../../../../components/common/DropdownFilter/DropdownFilter';
import { FilterConfig } from '../../../../components/common/DropdownFilter/types';
import Tags from '../../../../components/common/Tags';
import { Button } from '../../../../components/ui/Button/Button';
import { queryFilters } from '../../../../utils/queryFilters';
import { generatePreviewFullName } from '../../../Drivers/Modals/helpers';
import { DriverAndVehicle } from '../../types';
import S from './SearchEntityStyles';
import Item from './Item';
import useDriverThumbnail from '../../../../theme/useDriverThumbnail';
import useVehicleThumbnail from '../../../../theme/useVehicleThumbnail';

type Props = {
  onSubmit: () => void;
  onCancel: () => void;
  fleetId: string;
  entity: string;
  getQuery: (fleetId: string, filters: string) => UseQueryResult<any, any>;
  setEntity: (entity: DriverAndVehicle) => void;
};

const SEARCH_CONFIG = [
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      // format of key should be [param]_[order<ASC|DESC>]
      { score_DESC: 'Score: most' },
      { score_ASC: 'Score: least' },
    ],
  },
] as FilterConfig[];

const generateEntityTitle = (data: DriverAndVehicle[] | undefined, entity: string): string => {
  if (!data?.length) {
    return `${entity.toUpperCase()}${entity.slice(1)}s`;
  }
  const isSingle = data.length === 1;
  return `${data.length} ${isSingle ? entity : `${entity}s`}`;
};

function SearchEntity({
  onSubmit,
  onCancel,
  fleetId,
  getQuery,
  entity,
  setEntity,
}: Props): JSX.Element {
  const vehicleThumbnail = useVehicleThumbnail();
  const driverThumbnail = useDriverThumbnail();
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, isFetching } = getQuery(fleetId, queryFilters(filters));

  const filteredResults = searchInput
    ? data?.results?.filter((item: DriverAndVehicle) => {
      const full = `${item.firstName || ''} ${item.lastName || ''} ${item.licensePlateNumber || ''}`.toLowerCase();
      return full.includes(searchInput.toLowerCase());
    })
    : data?.results;
  const [selectedEntity, setselectedEntity] = useState<DriverAndVehicle | null>(null);

  const handleEntityAssignment = (driver: DriverAndVehicle): void => {
    setEntity(driver);
    onSubmit();
  };

  const handleEntitySelection = (driver: DriverAndVehicle): void => {
    setselectedEntity(() => driver);
  };

  return (
    <S.Wrapper>
      <div className="titles">
        <div className="action-bar">
          <div className="bar">
            <div className="assignment-container">
              <div className="entity-overview">
                {selectedEntity && (
                  (entity === 'driver') ? (
                    <Tags.Driver
                      imageSrc={selectedEntity?.avatarUrl || driverThumbnail}
                      name={
                        generatePreviewFullName(selectedEntity?.firstName, selectedEntity?.lastName)
                      }
                      licenseName={selectedEntity?.licenseName as string}
                    />
                  ) : (
                    <Tags.Vehicle
                      imageSrc={vehicleThumbnail}
                      licensePlateNumber={selectedEntity?.licensePlateNumber}
                      model={selectedEntity?.model}
                    />
                  )
                )}
                {!selectedEntity && (
                  <div className="entity-placeholder"> {`Choose ${entity} from below`} </div>
                )}
              </div>
            </div>
            <div className="buttons-container">
              <Button
                className="cancel-btn"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                className="confirm-btn"
                disabled={!selectedEntity}
                onClick={() => handleEntityAssignment(selectedEntity as DriverAndVehicle)}
              >
                {`Select ${entity}`}
              </Button>
            </div>
          </div>
        </div>
        <div className="entities-toolbar">
          <h3>{generateEntityTitle(filteredResults, entity)}</h3>
          <input
            className="search-input"
            type="text"
            placeholder={`Search ${entity}...`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {
            SEARCH_CONFIG.map((filterConfig: FilterConfig) => (
              <DropdownFilter
                key={filterConfig.type}
                config={filterConfig}
                filters={filters}
                setFilters={setFilters}
              />
            ))
          }
        </div>
      </div>
      <div className="entities-list">
        {isLoading && <div> Loading... </div>}
        {!isLoading && filteredResults && (
          <ul className="list-container">
              {!!filteredResults.length
                && filteredResults.map((item: DriverAndVehicle) => (
                  <Item
                    data={item}
                    key={item._id}
                    entity={entity}
                    isChecked={selectedEntity?._id === item._id}
                    selectEntity={handleEntitySelection}
                    assignEntity={handleEntityAssignment}
                  />
                ))}
              {!isFetching && !filteredResults.length && <div className="no-data">No {entity}s found</div>}
          </ul>
        )}
      </div>
      {/* Pagination */}
    </S.Wrapper>
  );
}

export default SearchEntity;
